import { useEffect, useRef, useState } from "react";
import DashboardContentLayout from "../components/dashboard-content-layout";
import { useNavigate, useParams } from "react-router-dom";
import {
  useCustomerDeleteOrchestratorControllerDelete,
  useAdminCustomerControllerGetOne,
  useAdminCustomerControllerSendInvite,
  useAdminCustomerControllerUpdate,
  useAdminLocationCustomerControllerGetAll,
  adminPutCustomerReqDtoSchema,
  ServiceTypeEnum,
} from "service";
import { useDialogStore } from "../../../state/dialog-store";
import {
  Box,
  Button,
  Divider,
  TextField,
  Typography,
  Tabs,
  Tab,
} from "@mui/material";
import { ControlledSelect, generateRoute, makeSxStyles } from "ui";
import { DateTime } from "luxon";
import { fieldLabelSx } from "@/shared/components/crm/form-typography";
import { useFormik } from "formik";
import {
  AppAccessEnum,
  appAccessEnumSelectOptions,
} from "service/types/enum/app-access-enum";
import { AdRoutes } from "../../../constants/ad-routes";
import { toast } from "react-toastify";
import {
  AdminPutCustomerReqDto,
  AdminShortLocationResDto,
} from "service/api/estabrookSchemas";
import { fullName } from "../../../utils/string-helpers";
import { Delete, Email } from "@mui/icons-material";
import { queryClient } from "../../../query-client";
import { queryKeyFn } from "service/api/estabrookContext";
import { toFormikValidationSchema } from "zod-formik-adapter";
import { z } from "zod";
import AddressDetailComponent from "../address-detail-component/address-detail-component";
import useEditStore from "../../../state/edit-store";
import { isNull, omitBy } from "lodash";
import CustomerNotesContainer from "../customer-notes/customer-notes";
import Navbar from "../../../components/navbar/navbar";
import HeaderItems, {
  HeaderItemsLeft,
  HeaderItemsRight,
} from "../../../shared/components/header-items/HeaderItems";
import useUnsavedChangesBlocker from "../../../shared/hooks/useUnsavedChangesBlocker";
import useEmailWarning from "../../../shared/hooks/useEmailWarning";
import {
  ensurePhonePrefix,
  handlePhoneChange,
} from "../../../shared/utils/phoneUtils";

// Custom validation schema that allows "+" for phone field
const customAdminPutCustomerReqDtoSchema = adminPutCustomerReqDtoSchema.extend({
  phone: z
    .string()
    .regex(/^(\+|[+]?[1-9]?\d{1,16})$/, "Must be a valid phone number.")
    .optional(),
});

const CustomerDetailPage = () => {
  const { customerId, addressId } = useParams<{
    customerId: string;
    addressId?: string;
  }>();
  const {
    selectedLocation,
    setSelectedLocation,
    isDirtyAddress,
    resetState,
    isValidAddress,
  } = useEditStore();
  const { isLoading, data } = useAdminCustomerControllerGetOne({
    pathParams: { customerId: customerId || "" },
  });
  const { data: locationsData, isLoading: isLocationDataLoading } =
    useAdminLocationCustomerControllerGetAll({
      pathParams: { customerId: customerId || "" },
    });

  const { mutate } = useAdminCustomerControllerUpdate({
    onSuccess: () => {
      toast.success("User Updated Successfully");
      queryClient.invalidateQueries(
        queryKeyFn({
          path: "/api/v1/admin/customers/{customerId}",
          operationId: "adminCustomerControllerGetOne",
          variables: { pathParams: { customerId: customerId || "" } },
        }),
      );

      queryClient.invalidateQueries(
        queryKeyFn({
          path: "/api/v1/admin/customers-locations",
          operationId: "adminCustomerLocationControllerGetAll",
          variables: {},
        }),
      );
    },
  });
  const { mutate: removeCustomer } =
    useCustomerDeleteOrchestratorControllerDelete({
      onSuccess: () => {
        toast.success("User Deleted Successfully");
        queryClient.invalidateQueries(
          queryKeyFn({
            path: "/api/v1/admin/customers-locations",
            operationId: "adminCustomerLocationControllerGetAll",
            variables: {},
          }),
        );
        setTimeout(() => {
          navigate(AdRoutes.customer);
        }, 10);
      },
    });

  const { mutate: sendEmail } = useAdminCustomerControllerSendInvite({
    onSuccess: () => {
      toast.success("Email Sent Successfully");
    },
  });

  const navigate = useNavigate();
  const styles = useStyles();
  const addressRef = useRef<{ updateAddres: () => void }>(null);

  const {
    values,
    handleBlur,
    handleChange,
    touched,
    errors,
    dirty,
    isValid,
    resetForm,
    setFieldValue,
    setFieldTouched,
  } = useFormik<AdminPutCustomerReqDto & { serviceType?: ServiceTypeEnum }>({
    initialValues: {
      firstName: "",
      lastName: "",
      email: "",
      phone: "+",
      serviceType: undefined,
      serviceAccess: undefined,
    },
    validationSchema: toFormikValidationSchema(
      customAdminPutCustomerReqDtoSchema,
    ),
    onSubmit: (values) =>
      mutate({
        body: {
          ...values,
          email: values.email ? values.email.toLowerCase() : undefined,
          phone: values.phone === "+" ? undefined : values.phone,
        },
        pathParams: { customerId: customerId || "" },
      }),
  });

  const [originalFormValues, setOriginalFormValues] = useState<
    (AdminPutCustomerReqDto & { serviceType?: ServiceTypeEnum }) | null
  >(null);

  const { setInitialEmail, showEmailWarning } = useEmailWarning({
    currentEmail: values.email?.toLowerCase(),
  });

  // Custom dirty state that ignores email casing changes
  // TODO: Refactor this in the future
  const isFormDirty = () => {
    if (!originalFormValues) return dirty;

    const currentValues = {
      firstName: values.firstName,
      lastName: values.lastName,
      email: values.email?.toLowerCase() || "",
      phone: values.phone,
      serviceType: values.serviceType,
      serviceAccess: values.serviceAccess,
    };

    const originalValues = {
      firstName: originalFormValues.firstName,
      lastName: originalFormValues.lastName,
      email: originalFormValues.email?.toLowerCase() || "",
      phone: originalFormValues.phone,
      serviceType: originalFormValues.serviceType,
      serviceAccess: originalFormValues.serviceAccess,
    };

    return JSON.stringify(currentValues) !== JSON.stringify(originalValues);
  };

  useUnsavedChangesBlocker(isFormDirty() || isDirtyAddress);

  useEffect(() => {
    if (!isLoading && data) {
      const customerData = omitBy(data?.data, isNull) as AdminPutCustomerReqDto;
      const formattedPhone = ensurePhonePrefix(customerData.phone);

      // Get the most common service type from customer's locations
      const serviceType =
        locationsData?.data && locationsData.data.length > 0
          ? (locationsData.data[0].type as ServiceTypeEnum) ||
            ServiceTypeEnum.onDemand
          : ServiceTypeEnum.onDemand;

      const formValues = {
        ...customerData,
        phone: formattedPhone,
        serviceType,
      };

      resetForm({ values: formValues });
      setOriginalFormValues(formValues);
      setInitialEmail((data?.data?.email || "").toLowerCase());
    }
  }, [data, isLoading, locationsData, setInitialEmail]);

  useEffect(() => {
    if (!isLocationDataLoading && locationsData) {
      if (addressId) {
        setSelectedLocation(addressId);
      } else {
        // Landed without an address in the URL (e.g. "View Customer" from a
        // lead → /customer/:id). Redirect to the first address so the address
        // detail + Customer Notes (both keyed off the URL addressId) render.
        const firstLocationId = (
          locationsData.data as AdminShortLocationResDto[]
        )[0]?.id;
        if (firstLocationId) {
          setSelectedLocation(firstLocationId);
          navigate(
            generateRoute(AdRoutes.customerDetailAddress, {
              customerId: customerId || "",
              addressId: firstLocationId,
            }),
            { replace: true },
          );
        }
      }
    }
  }, [locationsData, isLocationDataLoading]);

  const { setDialog } = useDialogStore();

  const onUpdateCustomer = () => {
    if (dirty) {
      mutate({
        body: {
          ...values,
          phone: values.phone === "+" ? undefined : values.phone,
        },
        pathParams: { customerId: customerId || "" },
      });
    }
    if (isDirtyAddress) {
      addressRef.current?.updateAddres();
    }
  };

  const onDeleteCustomer = () => {
    setDialog({
      title: "Delete Customer",
      description: `Are you sure you want to delete ${values.firstName} ${values.lastName}?<br />This will permanently remove all related information, and cannot be undone.`,
      confirmText: "Delete",
      withCancelAction: true,
      cancelText: "Cancel",
      sx: {
        "& .MuiDialog-container": {
          "& .MuiPaper-root": {
            width: "100%",
            maxWidth: "400px",
          },
        },
      },
      onConfirm: async (onClose) => {
        removeCustomer({ pathParams: { customerId: customerId || "" } });
        onClose();
      },
    });
  };

  const selectLocation = (locationId: string) => {
    setSelectedLocation(locationId);
    if (locationId) {
      // Keep the URL's addressId in sync with the selected tab so the
      // address details and Customer Notes (both keyed off the URL) switch
      // to the newly selected address instead of staying on the previous one.
      navigate(
        generateRoute(AdRoutes.customerDetailAddress, {
          customerId: customerId || "",
          addressId: locationId,
        }),
      );
    }
  };

  const onChangeTab = (_event: React.SyntheticEvent, newValue: string) => {
    if (isDirtyAddress) {
      setDialog({
        title: "Unsaved changes",
        description:
          "You have unsaved changes. Are you sure you want to leave?",
        confirmText: "Leave",
        withCancelAction: true,
        cancelText: "Cancel",
        onConfirm: async (onClose) => {
          onClose();
          selectLocation(newValue);
        },
      });
    } else {
      selectLocation(newValue);
    }
    if (newValue === null) {
      navigate(
        generateRoute(AdRoutes.addAddress, { customerId: customerId || "" }),
      );
    }
  };

  const handleEmailBlur = (event: React.FocusEvent<HTMLInputElement>) => {
    const lowercaseEmail = event.target.value.toLowerCase();
    setFieldValue("email", lowercaseEmail);
    handleBlur(event);
  };

  const setTab = () => {
    if (locationsData && locationsData.data?.length) {
      setSelectedLocation(locationsData.data[0].id);
    } else {
      setSelectedLocation(null);
    }
  };

  useEffect(() => {
    return () => {
      resetState();
    };
  }, []);

  return (
    <DashboardContentLayout>
      <Navbar showAccountMenu>
        <HeaderItems>
          <HeaderItemsLeft
            breadcrumbs={[
              {
                label: "Customer Management",
                url: AdRoutes.customer,
              },
              {
                label: fullName(data?.data),
              },
            ]}
          />
          <HeaderItemsRight>
            <Button
              disabled={
                !isValid ||
                !isValidAddress ||
                (!isFormDirty() && !isDirtyAddress)
              }
              variant="contained"
              onClick={onUpdateCustomer}
              data-testid="save-changes-button"
            >
              Save changes
            </Button>
          </HeaderItemsRight>
        </HeaderItems>
      </Navbar>
      <Typography sx={styles.sectionTitle}>Customer Info</Typography>
      {data?.data?.createdAt && (
        <Typography sx={styles.createdSubtitle}>
          {`Created ${DateTime.fromISO(data.data.createdAt).toFormat(
            "MM/dd/yyyy",
          )} ${data.data.lead ? "from Lead" : "by Admin"}`}
        </Typography>
      )}
      <Box sx={styles.customerDetailContainer}>
        <Typography sx={fieldLabelSx}>First Name *</Typography>
        <TextField
          id="firstName"
          type="text"
          value={values.firstName}
          onChange={handleChange}
          onBlur={handleBlur}
          helperText={touched.firstName && errors.firstName}
          error={touched.firstName && !!errors.firstName}
          margin="dense"
          variant="outlined"
          inputProps={{ maxLength: 30 }}
          fullWidth
        />
        <Typography sx={fieldLabelSx}>Last Name *</Typography>
        <TextField
          id="lastName"
          type="text"
          value={values.lastName}
          onChange={handleChange}
          onBlur={handleBlur}
          helperText={touched.lastName && errors.lastName}
          error={touched.lastName && !!errors.lastName}
          margin="dense"
          variant="outlined"
          inputProps={{ maxLength: 30 }}
          fullWidth
        />
        <Typography sx={fieldLabelSx}>Email Address *</Typography>
        <Box sx={{ display: "flex", flexDirection: "column", gap: "8px" }}>
          <TextField
            id="email"
            type="email"
            value={values.email}
            onChange={handleChange}
            onBlur={handleEmailBlur}
            helperText={touched.email && errors.email}
            error={touched.email && !!errors.email}
            margin="dense"
            variant="outlined"
            fullWidth
          />
          {showEmailWarning && (
            <Box
              sx={{
                ...styles.invalidSelection,
                marginTop: touched.email && !!errors.email ? "12px" : "0px",
              }}
            >
              <Typography
                fontWeight={600}
                mb="5px"
                data-testid="invalid-selection-header"
              >
                Updating the customer’s email revokes the customer’s app access.
                We’ll send a new invite to the customer’s updated address.
              </Typography>
            </Box>
          )}
        </Box>
        <Typography sx={fieldLabelSx}>Phone number *</Typography>
        <TextField
          id="phone"
          type="tel"
          value={values.phone}
          onChange={(e) => {
            const formattedPhone = handlePhoneChange(e.target.value);
            setFieldValue("phone", formattedPhone);
            setFieldTouched("phone", true);
          }}
          onBlur={handleBlur}
          helperText={touched.phone && errors.phone}
          error={touched.phone && !!errors.phone}
          margin="dense"
          variant="outlined"
          fullWidth
        />
        <Typography sx={fieldLabelSx}>Email Verified</Typography>
        <Typography color="text.secondary">
          {data?.data?.emailConfirmed ? "Yes" : "No"}
        </Typography>
        {!data?.data?.emailConfirmed && (
          <>
            <Typography sx={fieldLabelSx}>Send Invitation Email</Typography>
            <Button
              variant="outlined"
              sx={{ justifySelf: "flex-start" }}
              startIcon={<Email />}
              onClick={() => {
                sendEmail({ pathParams: { customerId: customerId || "" } });
              }}
            >
              Send
            </Button>
          </>
        )}
        <Typography sx={fieldLabelSx}>Service Access</Typography>
        <ControlledSelect
          placeholderSelectEnabled={false}
          placeholdeShow={false}
          placeholder="Service Access"
          value={
            values.serviceAccess ? AppAccessEnum.active : AppAccessEnum.disabled
          }
          options={appAccessEnumSelectOptions}
          onChange={(value) => {
            const isActive = value === AppAccessEnum.active;
            setFieldValue("serviceAccess", isActive);
            setFieldTouched("serviceAccess", true);
          }}
        />
        <Typography sx={fieldLabelSx} alignSelf="start">
          Delete Customer
        </Typography>
        <Box display="grid" gap="20px" alignItems="flex-start">
          <Typography variant="caption">
            Are you sure you want to delete {values.firstName} {values.lastName}
            ?
            <br />
            This will permanently remove all related information, and cannot be
            undone.
          </Typography>
          <Button
            color="error"
            variant="outlined"
            sx={styles.deleteButton}
            onClick={onDeleteCustomer}
            startIcon={<Delete />}
            data-testid="delete-user-button"
          >
            Delete
          </Button>
        </Box>
        <Divider sx={styles.divider} />
      </Box>
      <Box sx={{ m: "40px 0" }}>
        <Tabs
          value={selectedLocation ? selectedLocation : false}
          onChange={onChangeTab}
          variant="scrollable"
          scrollButtons="auto"
          allowScrollButtonsMobile
        >
          {locationsData &&
            (locationsData.data as AdminShortLocationResDto[]).map((a) => (
              <Tab key={a.id} label={a.address} value={a.id} />
            ))}
          <Tab
            label="+ Add new Address"
            value={null}
            data-testid="add-new-address"
          />
        </Tabs>
      </Box>
      {selectedLocation && (
        <AddressDetailComponent
          ref={addressRef}
          addressId={selectedLocation}
          setTab={setTab}
          customerServiceAccess={values.serviceAccess}
        />
      )}
      <Divider sx={styles.divider} />
      {addressId && <CustomerNotesContainer />}
    </DashboardContentLayout>
  );
};

const useStyles = makeSxStyles(() => ({
  sectionTitle: {
    fontWeight: 400,
    fontSize: "22px",
    lineHeight: "28px",
    color: "#c5c6c9",
    mt: "20px",
  },
  createdSubtitle: {
    fontSize: "14px",
    fontWeight: 500,
    lineHeight: "20px",
    color: "#c5c6c9",
    mt: "8px",
  },
  divider: {
    gridColumn: "1 / 3",
  },
  customerDetailContainer: {
    pt: "20px",
    display: "grid",
    // Match FieldRow (label 260 / gap 24 / input 360) for consistent alignment.
    gridTemplateColumns: "260px 360px",
    columnGap: "24px",
    rowGap: "16px",
    alignItems: "center",
  },
  deleteButton: {
    maxWidth: "150px",
  },
  invalidSelection: {
    width: "100%",
    padding: "16px",
    backgroundColor: "#FFB4AB",
    color: "#690005",
    borderRadius: "4px",
  },
}));

export default CustomerDetailPage;
